// The Questions panel: docked to the top edge of the composer, one control with
// it, never a form inside the transcript. The transcript stays a record of what
// was said — the answer leaves here as an ordinary user message.
//
// Shape follows the research on #2732: one question at a time (up to four),
// plain rows with no boxes, the CLIENT owning "Something else…", Skip always
// present, never blocking the composer, and no auto-submit on selection
// (arrow keys move AND select in a radiogroup — WCAG 3.2.2).
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useAuiState } from '@assistant-ui/react';
import { Maximize2Icon } from 'lucide-react';
import { useOptionalWorkspace } from './context';
import { WorkspaceIcon } from './icons';
import {
  composeQuestionAnswer,
  emptyDraft,
  hasAskedQuestions,
  type AskedQuestion,
  type QuestionDraft,
} from './question-contract';
import {
  hasOptionImages,
  optionImage,
  previewImageIds,
  screenshotImages,
  type OptionImages,
} from './question-images';

import { formatCopy } from '@/i18n/ns';
import { useQuestionCopy } from './question-copy';

/* ---------------------------------------------------------------- put away */
// The panel is dismissed from the footer and reopened from the transcript
// marker, which are different subtrees. One module-level store keeps them in
// step without threading state through the app shell, and holds the half-filled
// answer too: putting the panel away must not throw away what was chosen.
export interface AskProgress {
  index: number;
  drafts: QuestionDraft[];
}
const putAway = new Set<string>();
const progress = new Map<string, AskProgress>();
const listeners = new Set<() => void>();
const announce = () => listeners.forEach((listener) => listener());
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export const putAwayAsk = (id: string) => {
  putAway.add(id);
  announce();
};
export const reopenAsk = (id: string) => {
  putAway.delete(id);
  announce();
};
export const readAskProgress = (id: string) => progress.get(id);
/** Written on every keystroke, so it must never notify: the panel already
 * holds this state, and the store is only where it survives a put-away. */
export const writeAskProgress = (id: string, value: AskProgress) => {
  progress.set(id, value);
};
/** Everything but the open ask is finished with — answered, or replaced. */
export function forgetAsksExcept(id: string | null) {
  const stale = [...putAway, ...progress.keys(), ...picks.keys()].filter((key) => key !== id);
  if (!stale.length) return;
  for (const key of stale) {
    putAway.delete(key);
    progress.delete(key);
    picks.delete(key);
  }
  announce();
}
export function useAskPutAway(id: string | null) {
  return useSyncExternalStore(
    subscribe,
    () => (id !== null && putAway.has(id)) || false,
    () => false,
  );
}

/* ---------------------------------------------------------- choices canvas */
// The canvas sits outside the thread runtime (ui/workspace/shell.tsx), so the
// dock, which can read the thread, publishes the open ask and its option
// pictures here for the choices canvas (question-choices-canvas.tsx).
export interface OpenAsk extends PendingAsk {
  images: OptionImages;
}
let openAsk: OpenAsk | null = null;
export function publishOpenAsk(value: OpenAsk | null) {
  if (openAsk === value) return;
  openAsk = value;
  announce();
}
export function useOpenAsk() {
  return useSyncExternalStore(
    subscribe,
    () => openAsk,
    () => null,
  );
}
// "Use this" in the canvas is the same as ticking that option in the panel.
// The panel holds its answer in its own state, so it remounts from the stored
// progress, on that question, to carry on (Next, Submit) as usual.
const picks = new Map<string, number>();
export function pickAskOption(ask: PendingAsk, index: number, label: string) {
  const question = ask.questions[index];
  if (!question) return;
  const held = progress.get(ask.toolCallId);
  const drafts = ask.questions.map((_, at) => held?.drafts[at] ?? emptyDraft());
  if (!drafts[index].picked.includes(label))
    drafts[index] = pickOption(drafts[index], label, question.multiSelect === true);
  progress.set(ask.toolCallId, { index, drafts });
  picks.set(ask.toolCallId, (picks.get(ask.toolCallId) ?? 0) + 1);
  putAway.delete(ask.toolCallId);
  announce();
}
function useAskPicks(id: string | null) {
  return useSyncExternalStore(
    subscribe,
    () => (id === null ? 0 : (picks.get(id) ?? 0)),
    () => 0,
  );
}

/* ------------------------------------------------------------ pending ask */
export interface PendingAsk {
  toolCallId: string;
  questions: AskedQuestion[];
  language?: string;
}

// An ask is pending while the newest message is the assistant turn that made
// the call: any user message after it — chosen here or simply typed — is the
// answer, so the panel goes. Nothing is stored, so a reload restores it. A
// retried ask leaves its failed call in the message, so the ask is the last
// call that has questions.
const pendingAskKey = (state: {
  thread: { isRunning: boolean; messages: readonly unknown[] };
}): string => {
  if (state.thread.isRunning) return '';
  const messages = state.thread.messages as readonly {
    role: string;
    content: readonly { type: string; toolName?: string; toolCallId?: string; result?: unknown }[];
  }[];
  const last = messages[messages.length - 1];
  if (last?.role !== 'assistant') return '';
  const call = [...last.content].reverse().find(hasAskedQuestions);
  if (!call?.toolCallId) return '';
  const output = call.result as { questions: AskedQuestion[]; language?: string };
  return JSON.stringify({
    toolCallId: call.toolCallId,
    questions: output.questions,
    language: output.language,
  });
};

/** The open ask, or null. Serialized through the selector so the subscription
 * compares a string: returning a fresh object would re-render on every tick. */
export function usePendingAsk(): PendingAsk | null {
  const key = useAuiState(pendingAskKey);
  return useMemo(() => (key ? (JSON.parse(key) as PendingAsk) : null), [key]);
}

/* ------------------------------------------------------------------ panel */
const controls = (root: HTMLElement | null) =>
  root
    ? [...root.querySelectorAll<HTMLInputElement>('input[type="radio"],input[type="checkbox"]')]
    : [];

function moveFocus(root: HTMLElement | null, delta: number) {
  const inputs = controls(root);
  if (!inputs.length) return;
  const at = inputs.findIndex((input) => input === document.activeElement);
  inputs[(at + delta + inputs.length) % inputs.length]?.focus();
}

const wantsAutoFocus = () => {
  try {
    return !window.matchMedia?.('(pointer: coarse)').matches;
  } catch {
    return true;
  }
};

function pickOption(draft: QuestionDraft, label: string, multiSelect: boolean): QuestionDraft {
  if (!multiSelect) return { ...draft, picked: [label], otherPicked: false };
  const picked = draft.picked.includes(label)
    ? draft.picked.filter((value) => value !== label)
    : [...draft.picked, label];
  return { ...draft, picked };
}

/** Typed words are part of the answer whether or not Other was ticked (question-contract.ts). */
const otherIncluded = (draft: QuestionDraft) => draft.otherPicked || draft.other.trim().length > 0;

function pickOther(draft: QuestionDraft, multiSelect: boolean): QuestionDraft {
  // Unticking Other removes its words, so what shows as ticked is what is sent.
  if (multiSelect && otherIncluded(draft)) return { ...draft, otherPicked: false, other: '' };
  return { ...draft, otherPicked: true, picked: multiSelect ? draft.picked : [] };
}

function OptionRow({
  label,
  description,
  image,
  multiSelect,
  name,
  checked,
  onPick,
  children,
}: {
  label: string;
  description?: string;
  /** The option's picture (a screenshot of the variant it names). */
  image?: string;
  multiSelect: boolean;
  name: string;
  checked: boolean;
  onPick: () => void;
  children?: React.ReactNode;
}) {
  return (
    <label className={`ws-ask-row${checked ? ' selected' : ''}${image ? ' with-image' : ''}`}>
      <input
        type={multiSelect ? 'checkbox' : 'radio'}
        name={name}
        value={label}
        checked={checked}
        onChange={onPick}
        // A ticked radio fires no change when clicked again; Other still needs that click (only my words).
        onClick={!multiSelect && checked ? onPick : undefined}
      />
      {image && (
        <span className="ws-ask-shot">
          <img src={image} alt="" />
        </span>
      )}
      <span className="ws-ask-row-body">
        {children ?? <span className="ws-ask-row-label">{label}</span>}
        {description && !children && <small>{description}</small>}
      </span>
    </label>
  );
}

/**
 * Presentational: one question of the pending ask. The dock owns which question
 * is showing and what leaves as a message, so these cases need no runtime.
 */
export function QuestionPanel({
  questions,
  language,
  autoFocus = true,
  restore,
  images = NO_IMAGES,
  onExpand,
  onProgress,
  onSubmit,
  onClose,
}: {
  questions: AskedQuestion[];
  language?: string;
  autoFocus?: boolean;
  /** A half-filled answer to resume — the panel was put away, not abandoned. */
  restore?: { index: number; drafts: QuestionDraft[] } | undefined;
  /** Option pictures by screenshotId; options without one stay text rows. */
  images?: OptionImages;
  /** Show this question's pictures larger, in the canvas. */
  onExpand?: (index: number) => void;
  onProgress?: (value: { index: number; drafts: QuestionDraft[] }) => void;
  onSubmit: (text: string) => void;
  onClose: () => void;
}) {
  const { locale, copy } = useQuestionCopy(language);
  const [index, setIndex] = useState(() => restore?.index ?? 0);
  const [drafts, setDrafts] = useState<QuestionDraft[]>(
    () => restore?.drafts ?? questions.map(emptyDraft),
  );
  // A touch device has no pointer to park: taking focus there only raises the
  // keyboard and paints a focus ring nobody asked for.
  const [coarse] = useState(() => !wantsAutoFocus());
  const submitted = useRef(false);
  const takeFocus = autoFocus && !coarse;
  const groupRef = useRef<HTMLFieldSetElement>(null);
  const otherRef = useRef<HTMLInputElement>(null);
  const focusOther = useRef(false);
  const question = questions[index];
  const draft = drafts[index] ?? emptyDraft();
  const multiSelect = question?.multiSelect === true;
  const last = index === questions.length - 1;
  const name = `ws-ask-${index}`;

  const update = useCallback(
    (next: QuestionDraft) => setDrafts((all) => all.map((d, at) => (at === index ? next : d))),
    [index],
  );
  useEffect(() => {
    if (!submitted.current) onProgress?.({ index, drafts });
  }, [index, drafts, onProgress]);

  // Focus the first option when the panel opens over an empty composer, and
  // when moving between questions — but only while focus is already inside the
  // panel, so a draft in the composer is never interrupted.
  useEffect(() => {
    const root = groupRef.current;
    if (!root) return;
    const inside = root.contains(document.activeElement);
    if (index === 0 ? takeFocus : inside && !coarse) controls(root)[0]?.focus();
  }, [index, takeFocus, coarse]);

  useEffect(() => {
    if (!focusOther.current) return;
    focusOther.current = false;
    otherRef.current?.focus();
  }, [draft.otherPicked]);

  if (!question) return null;

  const chooseOther = () => {
    focusOther.current = !otherIncluded(draft) && wantsAutoFocus();
    update(pickOther(draft, multiSelect));
  };

  const keys = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    const inText = event.target === otherRef.current;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (inText || !multiSelect) return; // native radios already rove
      event.preventDefault();
      moveFocus(groupRef.current, event.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    // Number keys are the accelerator; they SELECT, never submit.
    if (!inText && /^[1-9]$/.test(event.key)) {
      const at = Number(event.key) - 1;
      if (at === question.options.length) {
        event.preventDefault();
        chooseOther();
      } else if (question.options[at]) {
        event.preventDefault();
        update(pickOption(draft, question.options[at].label, multiSelect));
      }
    }
  };

  // One handler for Next, Submit and Skip. It composes from the snapshot it is
  // given, never from `drafts`: after Skip clears this question, the last-question
  // branch would otherwise read the previous render's state and send the very
  // answer the user just skipped.
  const advance = (nextDrafts: QuestionDraft[] = drafts) => {
    if (nextDrafts !== drafts) setDrafts(nextDrafts);
    if (!last) {
      setIndex(index + 1);
      return;
    }
    submitted.current = true;
    onSubmit(composeQuestionAnswer(questions, nextDrafts, copy.noPreference) || copy.skipAnswer);
  };
  // Skip is "no preference for this one": it clears the question it leaves, so
  // Back shows it empty. Submission reports it as "no preference", or sends
  // SKIP_ANSWER when nothing in the panel was answered.
  const skip = () => advance(drafts.map((value, at) => (at === index ? emptyDraft() : value)));
  const visual = hasOptionImages(question, images);

  return (
    <form
      className="ws-ask-dock"
      aria-label={copy.title}
      lang={locale}
      onKeyDown={keys}
      onSubmit={(event) => {
        event.preventDefault();
        advance();
      }}
    >
      <div className="ws-ask-head">
        <span className="ws-ask-title">{copy.title}</span>
        {questions.length > 1 && (
          <span className="ws-ask-count">
            {formatCopy(copy.count, { current: index + 1, total: questions.length })}
          </span>
        )}
        <button type="button" className="ws-ask-close" aria-label={copy.putAway} onClick={onClose}>
          <WorkspaceIcon name="close" size={14} />
        </button>
      </div>
      <div className="ws-ask-body">
        {!takeFocus && index === 0 && (
          <p className="ws-sr-only" role="status" aria-live="polite">
            {formatCopy(questions.length === 1 ? copy.announceOne : copy.announceMany, {
              count: questions.length,
            })}
          </p>
        )}
        <fieldset className="ws-ask-group" ref={groupRef}>
          <legend className="ws-ask-question">{question.question}</legend>
          {multiSelect && <p className="ws-ask-hint">{copy.hint}</p>}
          {visual && onExpand && (
            <button type="button" className="ws-ask-expand" onClick={() => onExpand(index)}>
              <Maximize2Icon aria-hidden="true" />
              {copy.compare}
            </button>
          )}
          <div className={`ws-ask-rows${visual ? ' visual' : ''}`}>
            {question.options.map((option) => (
              <OptionRow
                key={option.label}
                label={option.label}
                description={option.description}
                image={optionImage(images, option.previewImage)}
                multiSelect={multiSelect}
                name={name}
                checked={draft.picked.includes(option.label)}
                onPick={() => update(pickOption(draft, option.label, multiSelect))}
              />
            ))}
            {/* Typed words go with a single choice, so in a single-choice question Other is its own control and
                shows as chosen beside the picked option instead of looking left out. */}
            <OptionRow
              label={copy.other}
              multiSelect={multiSelect}
              name={multiSelect ? name : `${name}-other`}
              checked={otherIncluded(draft)}
              onPick={chooseOther}
            >
              {draft.otherPicked || draft.other.length > 0 ? (
                <input
                  ref={otherRef}
                  type="text"
                  className="ws-ask-other-input"
                  aria-label={copy.other}
                  value={draft.other}
                  placeholder={copy.typeAnswer}
                  onChange={(event) => update({ ...draft, other: event.target.value })}
                />
              ) : (
                <span className="ws-ask-row-label placeholder">{copy.other}</span>
              )}
            </OptionRow>
          </div>
        </fieldset>
      </div>
      <div className="ws-ask-actions">
        {index > 0 && (
          <button type="button" className="ws-ask-back" onClick={() => setIndex(index - 1)}>
            {copy.back}
          </button>
        )}
        <span className="ws-ask-spacer" />
        <button type="button" className="ws-ask-skip" onClick={skip}>
          {copy.skip}
        </button>
        <button type="submit" className="ws-ask-submit">
          {last ? copy.submit : copy.next}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------- dock */
const NO_IMAGES: OptionImages = Object.freeze({});

/** The pending ask's option pictures, from the app_screenshot results in this
 * thread. The thread is read only while an ask names a picture. */
function useAskImages(ask: PendingAsk | null): OptionImages {
  const ids = useMemo(() => (ask ? previewImageIds(ask.questions) : []), [ask]);
  const messages = useAuiState((state) => (ids.length ? state.thread.messages : null));
  return useMemo(() => (messages ? screenshotImages(messages, ids) : NO_IMAGES), [messages, ids]);
}

/** Rendered by the thread's ComposerDock slot, immediately above the composer. */
export function WorkspaceQuestionDock() {
  const workspace = useOptionalWorkspace();
  const ask = usePendingAsk();
  const hidden = useAskPutAway(ask?.toolCallId ?? null);
  const draft = useAuiState((state) => state.composer.text.trim().length > 0);
  const openId = ask?.toolCallId ?? null;
  const picked = useAskPicks(openId);
  const images = useAskImages(ask);
  // Answered, or replaced by a newer ask: its half-filled answer is finished
  // with, and so is any record that its panel had been put away.
  useEffect(() => forgetAsksExcept(openId), [openId]);
  useEffect(() => publishOpenAsk(ask ? { ...ask, images } : null), [ask, images]);
  useEffect(() => () => publishOpenAsk(null), []);
  const remember = useCallback(
    (value: AskProgress) => {
      if (openId) writeAskProgress(openId, value);
    },
    [openId],
  );
  if (!workspace || !ask || hidden) return null;
  return (
    <QuestionPanel
      key={`${ask.toolCallId}:${picked}`}
      questions={ask.questions}
      language={ask.language}
      autoFocus={!draft}
      restore={readAskProgress(ask.toolCallId)}
      images={images}
      onExpand={(index) =>
        workspace.openCanvas({ kind: 'choices', id: ask.toolCallId, tab: String(index) })
      }
      onProgress={remember}
      onClose={() => putAwayAsk(ask.toolCallId)}
      onSubmit={(text) => workspace.send(text, workspace.turnContext, true)}
    />
  );
}

/** Whether this exact ask is the open one — the transcript marker offers to
 * bring it back only when it was put away, never for an answered question. */
export function useAskState(toolCallId: string | undefined) {
  const ask = usePendingAsk();
  const hidden = useAskPutAway(toolCallId ?? null);
  const pending = Boolean(toolCallId) && ask?.toolCallId === toolCallId;
  return { pending, reopenable: pending && hidden };
}
